import * as React from "react";
import * as ToastPrimitive from "@radix-ui/react-toast";
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAppDispatch, useAppSelector } from "@/store";
import { dismissToast } from "@/store/slices/uiSlice";
import { selectActiveToasts } from "@/store/selectors";
import type { ToastTone } from "@/store/slices/uiSlice";

const TONE_ICON: Record<ToastTone, React.ComponentType<{ className?: string }>> = {
  success: CheckCircle2,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const TONE_BG: Record<ToastTone, string> = {
  success: "bg-success/10 text-success border-success/30",
  error: "bg-error/10 text-error border-error/30",
  warning: "bg-warning/10 text-warning border-warning/30",
  info: "bg-grey-bg text-foreground border-border",
};

export function ToastViewport() {
  const toasts = useAppSelector(selectActiveToasts);
  const dispatch = useAppDispatch();

  return (
    <ToastPrimitive.Provider swipeDirection="right" duration={4500}>
      {toasts.map((t) => {
        const Icon = TONE_ICON[t.tone];
        return (
          <ToastPrimitive.Root
            key={t.id}
            duration={t.duration ?? 4500}
            onOpenChange={(open) => {
              if (!open) dispatch(dismissToast(t.id));
            }}
            className={cn(
              "rounded-card border p-4 shadow-artifact backdrop-blur",
              "data-[state=open]:animate-riseIn data-[state=closed]:opacity-0",
              "grid grid-cols-[auto_1fr_auto] gap-3 items-start max-w-sm",
              TONE_BG[t.tone],
            )}
          >
            <Icon className="w-5 h-5 mt-0.5 shrink-0" />
            <div>
              <ToastPrimitive.Title className="font-heading text-sm font-medium">
                {t.title}
              </ToastPrimitive.Title>
              {t.description && (
                <ToastPrimitive.Description className="text-xs mt-1 opacity-80">
                  {t.description}
                </ToastPrimitive.Description>
              )}
            </div>
            <ToastPrimitive.Close
              aria-label="Close"
              className="opacity-60 hover:opacity-100 transition rounded p-0.5"
            >
              <X className="w-3.5 h-3.5" />
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        );
      })}
      <ToastPrimitive.Viewport className="fixed bottom-4 right-4 z-[10100] flex flex-col gap-2 w-fit outline-none" />
    </ToastPrimitive.Provider>
  );
}
